<script setup lang="ts">
import { computed } from "vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";

// Import shared components
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import VerticalSpacing from "@/views/blocks/shared/section/layout/spacing/VerticalSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import Media from "@/views/blocks/shared/section/media/media/Media.vue";
import Features from "@/views/blocks/shared/section/features/Features.vue";
import Feature from "@/views/blocks/shared/section/features/Feature.vue";

import { ContainedInPanelProps } from "./ContainedInPanelProps";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";

const props = defineProps<{
    block: PageBlock<ContainedInPanelProps>;
    pageColors: CssVarColors;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section
        :id="block.webId"
        :background-color="styleValues.background"
        :scrollAnimationType="scrollAnimationType"
    >
        <VerticalSpacing class="flex flex-col lg:flex-row gap-y-6 lg:gap-y-0">
            <!-- Content Column (Left) -->
            <div class="lg:w-1/2 flex justify-start items-center px-6 lg:px-0">
                <div class="flex flex-col gap-8 max-w-2xl mx-auto lg:ml-auto lg:mr-0 lg:px-8">
                    <div
                        v-if="
                            !dataValues.tag.isHidden ||
                            !dataValues.title.isHidden ||
                            !dataValues.description.isHidden
                        "
                        class="flex flex-col gap-4 lg:gap-6"
                    >
                        <EditableElement
                            v-if="!dataValues.tag.isHidden"
                            :blockId="block.id"
                            field-name="tag"
                        >
                            <p :style="{ color: styleValues.brand }" class="font-semibold">
                                {{ dataValues.tag.data.text }}
                            </p>
                        </EditableElement>

                        <EditableElement
                            v-if="!dataValues.title.isHidden"
                            :blockId="block.id"
                            field-name="title"
                        >
                            <Heading
                                :text="dataValues.title.data.text"
                                :tag="dataValues.title.data.tag"
                                :text-color="styleValues.text"
                                class="text-4xl sm:text-5xl"
                            />
                        </EditableElement>

                        <EditableElement
                            v-if="!dataValues.description.isHidden"
                            :blockId="block.id"
                            field-name="description"
                        >
                            <Paragraph
                                :text="dataValues.description.data.text"
                                :text-color="styleValues.text"
                                class="text-lg opacity-70"
                            />
                        </EditableElement>
                    </div>

                    <!-- Border between description and features -->
                    <div
                        v-if="
                            (!dataValues.tag.isHidden ||
                                !dataValues.title.isHidden ||
                                !dataValues.description.isHidden) &&
                            dataValues.features.data.items.length > 0
                        "
                        class="hidden lg:block"
                        :style="{ borderTop: `1px solid ${styleValues.border}` }"
                    ></div>

                    <!-- Features List on desktop -->
                    <Features v-if="dataValues.features.data.items.length > 0" class="flex">
                        <EditableElement
                            v-for="feature in dataValues.features.data.items"
                            :key="feature.id"
                            :blockId="block.id"
                            :field-name="`features_${feature.id}`"
                            :hideable="false"
                        >
                            <Feature
                                :title="feature.title"
                                :description="feature.description"
                                :icon-src="feature.iconSrc"
                                :title-text-color="styleValues.text"
                                :description-text-color="styleValues.text"
                            />
                        </EditableElement>
                    </Features>
                </div>
            </div>

            <!-- Image Column (Right) -->
            <div class="flex justify-center items-center lg:ml-auto lg:w-1/2 pl-6 xl:pl-24">
                <EditableElement
                    v-if="!dataValues.image.isHidden"
                    :blockId="block.id"
                    field-name="image"
                >
                    <div
                        class="aspect-[16/9] ml-auto w-full max-w-4xl rounded-l-lg overflow-hidden"
                    >
                        <Media
                            :data="dataValues.image.data"
                            :image-width="896"
                            :image-height="504"
                            :video-width="896"
                            :video-height="504"
                        />
                    </div>
                </EditableElement>
            </div>
        </VerticalSpacing>
    </Section>
</template>
